import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { PlatformSummary } from '@worki/shared';
import { api } from '../../lib/api.js';
import { Spinner } from '../../components/ui.js';
import { NotificationRow, useNotifications } from './Notifications.js';
import { AdminCard, Stat } from './parts.js';
import { SECTIONS } from './sections.js';

/**
 * The first screen of the back office: how the platform is doing, what wants
 * attention today, and a door to each of the ten sections.
 *
 * The notification list is the point of the page. A person opening the admin is
 * almost always here because something happened — an account is waiting, a
 * payment failed — and making them pick the right section first is making them
 * guess. The five most pressing are on the home screen, and the section holds
 * the rest.
 */
const PREVIEW = 5;

export function PlatformHome() {
  const { t } = useTranslation();
  /* The arrow points the way the page reads, the way every other list on the
     site does it. */
  const Forward = document.documentElement.dir === 'rtl' ? ChevronLeft : ChevronRight;

  const summary = useQuery({
    queryKey: ['platform', 'summary'],
    queryFn: () => api.get<PlatformSummary>('/platform/summary'),
  });

  const notifications = useNotifications();
  const items = notifications.data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
        <Stat label={t('platform.businesses')} value={summary.data?.businesses} />
        <Stat label={t('platform.active')} value={summary.data?.active} tone="good" />
        <Stat label={t('platform.trialing')} value={summary.data?.trialing} tone="info" />
        <Stat label={t('platform.pastDue')} value={summary.data?.pastDue} tone="warn" />
        <Stat label={t('platform.signupsThisMonth')} value={summary.data?.signupsThisMonth} tone="muted" />
        <Stat label={t('platform.activeThisWeek')} value={summary.data?.activeThisWeek} tone="muted" />
      </div>

      <AdminCard
        title={t('admin.sections.notifications')}
        action={
          items.length > PREVIEW ? (
            <Link to="/platform/notifications" className="text-xs font-medium text-primary hover:underline">
              {t('common.viewAll')}
            </Link>
          ) : undefined
        }
      >
        {notifications.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <p className="text-xs text-muted-foreground py-2">{t('admin.notifications.empty')}</p>
        ) : (
          <div className="space-y-2">
            {items.slice(0, PREVIEW).map((item, index) => (
              <NotificationRow key={`${item.kind}-${item.businessId ?? index}`} item={item} />
            ))}
          </div>
        )}
      </AdminCard>

      {/* The cards are the phone's menu. From md up the sidebar lists the same
          sections, so the page does not say them twice. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:hidden">
        {SECTIONS.map(({ id, icon: Glyph }) => (
          <Link
            key={id}
            to={`/platform/${id}`}
            className="glass-card rounded-xl px-3 py-3 flex items-center gap-3 hover:border-primary/20 transition-colors"
          >
            <span className="h-9 w-9 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Glyph className="h-4 w-4" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium truncate">{t(`admin.sections.${id}`)}</span>
              <span className="block text-[11px] text-muted-foreground truncate">{t(`admin.about.${id}`)}</span>
            </span>
            <Forward className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          </Link>
        ))}
      </div>
    </div>
  );
}
